// ── JSONL persistence ───────────────────────────────────────────────────────

import { existsSync, mkdirSync, appendFileSync, readFileSync, readdirSync, unlinkSync } from 'node:fs';
import { join } from 'node:path';
import { homedir } from 'node:os';
import type { UsageSnapshot, ToolRecord, CompactionRecord, ErrorRecord, TurnRecord, SessionStats, TokenSummary, CacheSummary, ToolSummary } from './types.js';

const STATS_DIR = join(homedir(), '.pi', 'agent', 'pi-stats-insight');

export class StatsStore {
  private sessionId: string;
  private filePath: string;

  // In-memory aggregation (faster than scanning jsonl every time)
  private usageSnapshots: UsageSnapshot[] = [];
  private toolRecords: ToolRecord[] = [];
  private compactionRecords: CompactionRecord[] = [];
  private errorRecords: ErrorRecord[] = [];
  private turnRecords: TurnRecord[] = [];

  constructor(sessionId: string) {
    this.sessionId = sessionId;
    this.filePath = join(STATS_DIR, `${sessionId}.jsonl`);
    if (!existsSync(STATS_DIR)) mkdirSync(STATS_DIR, { recursive: true });
  }

  // ── Append records ────────────────────────────────────────────────────────

  appendUsage(snapshot: UsageSnapshot): void {
    this.usageSnapshots.push(snapshot);
    this._append({ _type: 'usage', ...snapshot });
  }

  appendTool(record: ToolRecord): void {
    this.toolRecords.push(record);
    this._append({ _type: 'tool', ...record });
  }

  appendCompaction(record: CompactionRecord): void {
    this.compactionRecords.push(record);
    this._append({ _type: 'compaction', ...record });
  }

  appendError(record: ErrorRecord): void {
    this.errorRecords.push(record);
    this._append({ _type: 'error', ...record });
  }

  appendTurn(record: TurnRecord): void {
    this.turnRecords.push(record);
    this._append({ _type: 'turn', ...record });
  }

  private _append(obj: Record<string, unknown>): void {
    try {
      appendFileSync(this.filePath, JSON.stringify(obj) + '\n', 'utf8');
    } catch {
      // Silently fail — stats are best-effort, never crash pi
    }
  }

  // ── Read session stats ────────────────────────────────────────────────────

  getStats(): SessionStats {
    // If jsonl exists, reload from disk (handles hot reload / session restore)
    if (existsSync(this.filePath) && this.usageSnapshots.length === 0) {
      this._loadFromDisk();
    }
    return {
      sessionId: this.sessionId,
      usageSnapshots: this.usageSnapshots,
      toolRecords: this.toolRecords,
      compactionRecords: this.compactionRecords,
      errorRecords: this.errorRecords,
      turnRecords: this.turnRecords,
    };
  }

  private _loadFromDisk(): void {
    try {
      const lines = readFileSync(this.filePath, 'utf8').split('\n').filter(Boolean);
      for (const line of lines) {
        const obj = JSON.parse(line);
        if (obj._type === 'usage') this.usageSnapshots.push(obj as UsageSnapshot);
        if (obj._type === 'tool') this.toolRecords.push(obj as ToolRecord);
        if (obj._type === 'compaction') this.compactionRecords.push(obj as CompactionRecord);
        if (obj._type === 'error') this.errorRecords.push(obj as ErrorRecord);
        if (obj._type === 'turn') this.turnRecords.push(obj as TurnRecord);
      }
    } catch {
      // Corrupted jsonl — start fresh
    }
  }

  // ── Aggregation ───────────────────────────────────────────────────────────

  getTokenSummary(): TokenSummary {
    const stats = this.getStats();
    let input = 0, output = 0, cacheRead = 0, cacheWrite = 0;
    let costInput = 0, costOutput = 0, costCacheRead = 0, costWrite = 0, costTotal = 0;
    for (const u of stats.usageSnapshots) {
      input += u.input;
      output += u.output;
      cacheRead += u.cacheRead;
      cacheWrite += u.cacheWrite;
      costInput += u.costInput;
      costOutput += u.costOutput;
      costCacheRead += u.costCacheRead;
      costWrite += u.costCacheWrite;
      costTotal += u.costTotal;
    }
    return {
      input, output, cacheRead, cacheWrite,
      total: input + output + cacheRead + cacheWrite,
      costInput, costOutput, costCacheRead, costWrite, costTotal,
    };
  }

  getCacheSummary(): CacheSummary {
    const stats = this.getStats();
    const byProvider: Record<string, { hitRate: number; cacheRead: number; cacheWrite: number; input: number; requests: number }> = {};
    let totalCacheRead = 0, totalCacheWrite = 0, totalInput = 0, totalRequests = 0;

    for (const u of stats.usageSnapshots) {
      totalCacheRead += u.cacheRead;
      totalCacheWrite += u.cacheWrite;
      totalInput += u.input;
      totalRequests++;

      if (!byProvider[u.provider]) {
        byProvider[u.provider] = { hitRate: 0, cacheRead: 0, cacheWrite: 0, input: 0, requests: 0 };
      }
      byProvider[u.provider].cacheRead += u.cacheRead;
      byProvider[u.provider].cacheWrite += u.cacheWrite;
      byProvider[u.provider].input += u.input;
      byProvider[u.provider].requests++;
    }

    // Calculate hit rate: cacheRead / (cacheRead + input + cacheWrite)
    const totalDenom = totalCacheRead + totalInput + totalCacheWrite;
    const hitRate = totalDenom > 0 ? totalCacheRead / totalDenom : -1;

    for (const [prov, data] of Object.entries(byProvider)) {
      const denom = data.cacheRead + data.input + data.cacheWrite;
      data.hitRate = denom > 0 ? data.cacheRead / denom : -1;
    }

    return { hitRate, cacheRead: totalCacheRead, cacheWrite: totalCacheWrite, totalRequests, byProvider };
  }

  getToolSummary(): ToolSummary {
    const stats = this.getStats();
    const byTool: Record<string, { calls: number; errors: number; avgMs: number; totalResultSize: number }> = {};
    let totalCalls = 0, totalErrors = 0, totalDuration = 0;

    for (const t of stats.toolRecords) {
      totalCalls++;
      totalDuration += t.durationMs;
      if (t.isError) totalErrors++;

      if (!byTool[t.toolName]) byTool[t.toolName] = { calls: 0, errors: 0, avgMs: 0, totalResultSize: 0 };
      byTool[t.toolName].calls++;
      byTool[t.toolName].totalResultSize += t.resultSize;
      if (t.isError) byTool[t.toolName].errors++;
    }

    for (const data of Object.values(byTool)) {
      data.avgMs = data.calls > 0 ? totalDuration / totalCalls : 0; // Simplified
    }

    return {
      totalCalls,
      totalErrors,
      avgDurationMs: totalCalls > 0 ? totalDuration / totalCalls : 0,
      byTool,
    };
  }

  // ── Reset ─────────────────────────────────────────────────────────────────

  reset(): void {
    this.usageSnapshots = [];
    this.toolRecords = [];
    this.compactionRecords = [];
    this.errorRecords = [];
    this.turnRecords = [];
    try { unlinkSync(this.filePath); } catch { /* already gone */ }
  }

  // ── Today's stats (across all sessions) ───────────────────────────────────

  static getTodaySummary(): { sessions: number; totalCost: number; totalTokens: number } {
    if (!existsSync(STATS_DIR)) return { sessions: 0, totalCost: 0, totalTokens: 0 };

    const today = new Date().toISOString().slice(0, 10);
    let sessions = 0, totalCost = 0, totalTokens = 0;

    for (const file of readdirSync(STATS_DIR).filter(f => f.endsWith('.jsonl'))) {
      try {
        const lines = readFileSync(join(STATS_DIR, file), 'utf8').split('\n').filter(Boolean);
        let hasTodayData = false;
        for (const line of lines) {
          const obj = JSON.parse(line);
          const tsDate = new Date(obj.ts).toISOString().slice(0, 10);
          if (tsDate === today) {
            hasTodayData = true;
            if (obj._type === 'usage') {
              totalCost += obj.costTotal ?? 0;
              totalTokens += (obj.input ?? 0) + (obj.output ?? 0) + (obj.cacheRead ?? 0) + (obj.cacheWrite ?? 0);
            }
          }
        }
        if (hasTodayData) sessions++;
      } catch { /* skip corrupted */ }
    }

    return { sessions, totalCost, totalTokens };
  }
}